/**
 * useHotkey — the app's one keyboard-shortcut primitive.
 *
 * Every operator shortcut in the ERP (Ctrl+S save, Ctrl+K palette, "g d" jump
 * to dashboard, ? cheatsheet…) goes through this hook. A single document-level
 * `keydown` listener is installed on first use and dispatches to every hook
 * instance registered while its `when` gate is truthy — so wiring N shortcuts
 * costs one real listener, not N.
 *
 * Supported spec forms:
 *   • Single stroke:   'ctrl+s', 'ctrl+shift+n', 'meta+enter', 'delete', '/', '?'
 *   • Chord sequence:  'g d', 'g i'  — two-key sequence, 500 ms window.
 *
 * "ctrl" is the platform "modifier" role — Mac uses ⌘ (Meta), everywhere else
 * uses Ctrl. Consumers write `ctrl+s`; the hook accepts either physical key at
 * runtime. Write `meta+…` only when you specifically want Cmd-only (rare — the
 * standard case is `ctrl+…`).
 *
 * Focus rule: a shortcut is skipped when the focused element is a text input
 * (`<input>` / `<textarea>` / `contenteditable`) UNLESS it uses a real modifier
 * — Ctrl+S still fires while the operator is typing a note.
 */
import { useEffect, useRef } from 'react';

/* -------------------------------------------------------------------------- */
/*  Public types                                                              */
/* -------------------------------------------------------------------------- */

export interface HotkeySpec {
  /**
   * Key spec. Single stroke ("ctrl+s") or chord ("g d"). Case-insensitive.
   * Modifier tokens: ctrl (Ctrl or ⌘), meta (⌘ only), shift, alt.
   */
  keys: string;
  /** Called when the shortcut fires. */
  handler: (event: KeyboardEvent) => void;
  /** Gate — false skips the shortcut without unregistering. Default true. */
  when?: boolean;
  /** Human-readable label for the cheatsheet. Optional. */
  description?: string;
}

/* -------------------------------------------------------------------------- */
/*  Platform detection                                                        */
/* -------------------------------------------------------------------------- */

const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform ?? '');

/* -------------------------------------------------------------------------- */
/*  Normalised binding                                                        */
/* -------------------------------------------------------------------------- */

interface StrokeBinding {
  key: string;               // lowercase — 's', 'enter', '/', 'delete', 'k'
  ctrlOrMeta: boolean;       // "ctrl" role — Ctrl on Win/Linux, ⌘ on Mac
  metaOnly: boolean;         // written as `meta+…` — only fires on ⌘
  shift: boolean;
  alt: boolean;
}

interface ChordBinding {
  first: StrokeBinding;
  second: StrokeBinding;
}

interface Binding {
  raw: string;               // as passed by the caller, for the cheatsheet
  kind: 'stroke' | 'chord';
  stroke?: StrokeBinding;
  chord?: ChordBinding;
}

interface Registration {
  binding: Binding;
  spec: HotkeySpec;
}

/* -------------------------------------------------------------------------- */
/*  Parsing                                                                   */
/* -------------------------------------------------------------------------- */

function parseStroke(text: string): StrokeBinding {
  const parts = text
    .split('+')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  const binding: StrokeBinding = {
    key: '',
    ctrlOrMeta: false,
    metaOnly: false,
    shift: false,
    alt: false,
  };
  for (const p of parts) {
    if (p === 'ctrl' || p === 'cmd' || p === 'mod' || p === 'control') {
      binding.ctrlOrMeta = true;
    } else if (p === 'meta' || p === 'super' || p === 'win') {
      binding.metaOnly = true;
    } else if (p === 'shift') {
      binding.shift = true;
    } else if (p === 'alt' || p === 'option' || p === 'opt') {
      binding.alt = true;
    } else {
      binding.key = p;
    }
  }
  return binding;
}

function parseBinding(keys: string): Binding {
  const trimmed = keys.trim();
  // A chord uses a single space between two non-modifier strokes.
  // We split on the FIRST space — `ctrl+shift+n` isn't a chord.
  if (trimmed.includes(' ') && !/\+\s/.test(trimmed)) {
    const [a, ...rest] = trimmed.split(/\s+/);
    const b = rest.join(' ');
    return {
      raw: trimmed,
      kind: 'chord',
      chord: { first: parseStroke(a), second: parseStroke(b) },
    };
  }
  return { raw: trimmed, kind: 'stroke', stroke: parseStroke(trimmed) };
}

/* -------------------------------------------------------------------------- */
/*  Stroke matching                                                           */
/* -------------------------------------------------------------------------- */

function eventKey(e: KeyboardEvent): string {
  // Normalise the browser's `key` value into our lowercase form.
  const k = e.key;
  if (!k) return '';
  const lower = k.toLowerCase();
  // Alias common names so consumers can write either form.
  if (lower === ' ') return 'space';
  if (lower === 'esc') return 'escape';
  if (lower === 'del') return 'delete';
  return lower;
}

function matchStroke(b: StrokeBinding, e: KeyboardEvent): boolean {
  if (eventKey(e) !== b.key) return false;
  // `meta+…` — Cmd on Mac, Windows key on Win/Linux. Must NOT be Ctrl.
  if (b.metaOnly) {
    if (!e.metaKey || e.ctrlKey) return false;
  } else if (b.ctrlOrMeta) {
    // "ctrl" role — Cmd on Mac, Ctrl elsewhere. Accept either physical key.
    const platformMod = IS_MAC ? e.metaKey : e.ctrlKey;
    if (!platformMod) return false;
  } else {
    if (e.ctrlKey || e.metaKey) return false;
  }
  // For punctuation ("?", "/", "!") the shift state is intrinsic to producing
  // the character on many layouts — matching it strictly would break `?` on
  // US keyboards. Only enforce shift when the key is alphanumeric.
  const enforceShift = b.key.length !== 1 || /^[a-z0-9]$/.test(b.key);
  if (enforceShift && b.shift !== e.shiftKey) return false;
  if (b.alt !== e.altKey) return false;
  return true;
}

function strokeHasModifier(b: StrokeBinding): boolean {
  return b.ctrlOrMeta || b.metaOnly || b.alt;
}

/* -------------------------------------------------------------------------- */
/*  Focus rule                                                                */
/* -------------------------------------------------------------------------- */

function isInTextField(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

/* -------------------------------------------------------------------------- */
/*  Singleton dispatcher                                                      */
/* -------------------------------------------------------------------------- */

const REGISTRATIONS = new Set<Registration>();

/** Pending chord state — cleared when the window elapses. */
let pendingChord: {
  first: StrokeBinding;
  timer: ReturnType<typeof setTimeout>;
} | null = null;

/** 500 ms is the standard chord window (matches Gmail/Linear). */
const CHORD_WINDOW_MS = 500;

let listenerInstalled = false;

function ensureListener() {
  if (listenerInstalled || typeof window === 'undefined') return;
  window.addEventListener('keydown', dispatch, true);
  listenerInstalled = true;
}

function clearChord() {
  if (!pendingChord) return;
  clearTimeout(pendingChord.timer);
  pendingChord = null;
}

function dispatch(e: KeyboardEvent) {
  // Ignore repeats — held-down keys shouldn't fire chord sequences repeatedly.
  if (e.repeat) return;

  // Modifier-only strokes (just Ctrl / Shift / Alt / Meta) are keypress noise
  // between real strokes — don't reset chord state on them.
  if (['control', 'shift', 'alt', 'meta'].includes(e.key.toLowerCase())) return;

  const inText = isInTextField(e.target);

  // --- Continuing a pending chord ---------------------------------------- //
  if (pendingChord) {
    const first = pendingChord.first;
    // Second stroke of a chord — try to match, then clear state.
    for (const reg of REGISTRATIONS) {
      if (reg.spec.when === false) continue;
      if (reg.binding.kind !== 'chord') continue;
      const c = reg.binding.chord!;
      // Chords are only initiated when the first stroke matched something —
      // but multiple chords may share a first stroke, so filter to those.
      if (!strokesEqual(c.first, first)) continue;
      if (matchStroke(c.second, e)) {
        e.preventDefault();
        clearChord();
        reg.spec.handler(e);
        return;
      }
    }
    // No chord matched — fall through to try single-stroke handlers on this
    // key too, so 'g' → typed-into-search doesn't get eaten forever.
    clearChord();
  }

  // --- Single-stroke handlers ------------------------------------------- //
  for (const reg of REGISTRATIONS) {
    if (reg.spec.when === false) continue;
    if (reg.binding.kind !== 'stroke') continue;
    const s = reg.binding.stroke!;
    if (!matchStroke(s, e)) continue;
    // Focus rule — text fields eat unmodified strokes.
    if (inText && !strokeHasModifier(s)) continue;
    e.preventDefault();
    reg.spec.handler(e);
    return;
  }

  // --- Starting a chord -------------------------------------------------- //
  // A chord's first stroke must be UNMODIFIED (like Gmail's "g" prefix) —
  // otherwise Ctrl+G on gaming keyboards would open the sequence unexpectedly.
  if (inText) return;
  for (const reg of REGISTRATIONS) {
    if (reg.spec.when === false) continue;
    if (reg.binding.kind !== 'chord') continue;
    const c = reg.binding.chord!;
    if (strokeHasModifier(c.first)) continue;
    if (!matchStroke(c.first, e)) continue;
    // Start the sequence — record the matched first stroke and set the
    // dismissal timer. Any second stroke within the window resolves it.
    e.preventDefault();
    pendingChord = {
      first: c.first,
      timer: setTimeout(clearChord, CHORD_WINDOW_MS),
    };
    return;
  }
}

function strokesEqual(a: StrokeBinding, b: StrokeBinding): boolean {
  return (
    a.key === b.key &&
    a.ctrlOrMeta === b.ctrlOrMeta &&
    a.metaOnly === b.metaOnly &&
    a.shift === b.shift &&
    a.alt === b.alt
  );
}

/* -------------------------------------------------------------------------- */
/*  Public hook                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Register a keyboard shortcut for the lifetime of the calling component.
 *
 * ```tsx
 * useHotkey({ keys: 'ctrl+s', handler: () => save() });
 * useHotkey({ keys: 'g d',    handler: () => navigate('/dashboard') });
 * useHotkey({ keys: '?',      handler: () => setOpen(true) });
 * ```
 */
export function useHotkey(spec: HotkeySpec): void {
  const { keys, when, description } = spec;
  // Latest-handler ref — every render captures a fresh closure, but the
  // stable wrapper below keeps our Registration in place so re-renders don't
  // rebind the singleton listener. This is the standard pattern.
  const handlerRef = useRef(spec.handler);
  handlerRef.current = spec.handler;
  useEffect(() => {
    ensureListener();
    const reg: Registration = {
      binding: parseBinding(keys),
      spec: {
        keys,
        when,
        description,
        handler: (e) => handlerRef.current(e),
      },
    };
    REGISTRATIONS.add(reg);
    return () => {
      REGISTRATIONS.delete(reg);
    };
  }, [keys, when, description]);
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** True when the client is running on macOS — used to render ⌘ vs Ctrl. */
export const isMacPlatform = (): boolean => IS_MAC;

/**
 * Format a raw key spec for display — 'ctrl+s' → '⌘S' on Mac, 'Ctrl S' on Win.
 * Chords render with a small gap between strokes: 'g d' → 'G then D'.
 */
export function formatHotkey(keys: string): string {
  const binding = parseBinding(keys);
  if (binding.kind === 'stroke') return renderStroke(binding.stroke!);
  const first = renderStroke(binding.chord!.first);
  const second = renderStroke(binding.chord!.second);
  return `${first} then ${second}`;
}

function renderStroke(s: StrokeBinding): string {
  const parts: string[] = [];
  if (s.metaOnly) parts.push('⌘');
  else if (s.ctrlOrMeta) parts.push(IS_MAC ? '⌘' : 'Ctrl');
  if (s.alt) parts.push(IS_MAC ? '⌥' : 'Alt');
  if (s.shift) parts.push(IS_MAC ? '⇧' : 'Shift');
  parts.push(renderKey(s.key));
  return parts.join(IS_MAC ? '' : ' ');
}

function renderKey(k: string): string {
  if (!k) return '';
  if (k === 'escape') return 'Esc';
  if (k === 'enter') return '↵';
  if (k === 'arrowleft') return '←';
  if (k === 'arrowright') return '→';
  if (k === 'arrowup') return '↑';
  if (k === 'arrowdown') return '↓';
  if (k === 'space') return 'Space';
  if (k === 'delete') return 'Del';
  if (k === 'backspace') return '⌫';
  if (k.length === 1) return k.toUpperCase();
  return k.charAt(0).toUpperCase() + k.slice(1);
}
